//@ts-check
import { getCurrency, is_gbp, formatPrice } from '../global/currency';
import { formatNumber } from '../utilities/numbers';

/**
 * @typedef {{
 *  id: string,
 *  active: boolean,
 *  variation: ?string
 * }} ConfiguratorOption
 *
 * @typedef {ConfiguratorOption[]} ConfiguratorOptions
 *
 */


class HutConfigurator extends HTMLElement {
    constructor() {
        super();

        this.hutId = this.dataset.hutId;
        this.hutTitle = this.dataset.hutTitle;
        this.key = `hut-configurator-${this.hutId}`;

        this.basePrice = parseFloat( this.getAttribute( 'data-base-price' ) ?? '0' );
        this.basePriceEuros = parseFloat( this.getAttribute( 'data-base-price-euros' ) ?? '0' );
        this.options = /** @type {HTMLElement[]}*/ ( [...this.querySelectorAll( '[data-option]' )] );

        this.includedOptions = /** @type {HTMLElement[]}*/ ( [...this.querySelectorAll( '[data-option-included]' )] );
        this.optionalOptions = /** @type {HTMLElement[]}*/ ( [...this.querySelectorAll( '[data-option-optional]' )] );
        this.variableOptions = /** @type {HTMLElement[]}*/ ( [...this.querySelectorAll( '[data-option-variable]' )] );

        this.totalSlot = this.querySelector( '[data-total] .configurator__price' );
        this.form = this.querySelector( '[data-form]' );
    }

    connectedCallback() {
        this.initialiseOptionals();
        this.initialiseVariations();

        this.loadOptions();


        document.addEventListener( 'currency_change', ( _e ) => {
            this.refreshSelection();
        } );

        this.querySelectorAll( '[data-action="save-quote"]' ).forEach(
            trigger => {
                const defaultText = trigger.innerHTML;
                trigger.addEventListener(
                    'click',
                    ( () => {
                        let isValid = this.validate();
                        this.saveOptions();
                        trigger.innerHTML = 'Saved!';
                        setTimeout( () => {
                            trigger.innerHTML = defaultText;
                        }, 1500 );
                    } ).bind( this )
                );
            }
        );

        this.querySelectorAll( '[data-action="send-quote"]' ).forEach(
            trigger => trigger.addEventListener(
                'click',
                ( () => this.toggleForm() ).bind( this )
            )
        );

        this.initialisePopovers();

        const cf7form = this.form?.querySelector( '.wpcf7-form' );
        if ( cf7form ) {
            cf7form.addEventListener( 'wpcf7submit', this.showCF7Message.bind( this ) );
        }
    }


    /**
     * Returns the contextual price based on the currency
     */
    getBasePrice() {
        const currency = getCurrency();
        if ( currency === 'GBP' ) {
            return this.basePrice;
        } else if ( currency === 'EUR' ) {
            return this.basePriceEuros;
        } else {
            return this.basePrice;
        }
    }

    /** Toggles visibility of the form */
    toggleForm( force = null ) {
        let show;

        if ( force !== null ) {
            show = force;
        } else {
            show = this?.form?.getAttribute( 'aria-hidden' ) === 'true';
        }

        this?.form?.setAttribute( 'aria-hidden', show ? 'false' : 'true' );

        if ( show ) {
            this?.form?.scrollIntoView( { behavior: 'smooth', block: 'nearest', inline: 'start' } );
        }
    }

    showCF7Message() {
        const message = this?.form?.querySelector( '.wpcf7-response-output' );
        if ( !message ) return;
        // Tried requestAnimationFrame, but for successful submissions, it was still trying to scroll too early?
        setTimeout( () => {
            message.scrollIntoView( { behavior: 'smooth', block: 'end', inline: 'start' } );
        }, 400 );
    }

    /** Updates the contact form with the chosen fields from the configurator */
    updateFormMessage() {
        const hutField = this?.form?.querySelector( '[name="your-hut-name"]' );
        const messageField = this?.form?.querySelector( '[name="your-quote"]' );
        hutField?.setAttribute( 'value', this.hutTitle ?? '' );
        messageField?.setAttribute( 'value', this.formatOptions() );
    }

    initialiseOptionals() {
        this.optionalOptions.forEach( option => {
            const toggle = option.querySelector( '[data-option-toggle]' );
            // @ts-ignore If this is an error, there's a problem with the template file
            toggle.addEventListener( 'click', () => {
                option.classList.toggle( 'configurator-option--active' );
                this.updateTotal();
                this.updateFormMessage();
            } );
        } );
    }

    /** Getter for the currently selected options */
    initialiseVariations() {
        this.variableOptions.forEach( option => {
            const variations = /** @type {NodeListOf<HTMLInputElement>} */ ( option.querySelectorAll( '[data-option-variable] [data-variation]' ) );
            variations.forEach( vari => {
                vari.addEventListener( 'input', () => {
                    let price = vari.getAttribute( 'data-variation-price' ) ?? '0';
                    let euroPrice = vari.getAttribute( 'data-variation-price-euros' ) ?? '0';
                    const messageSlot = option.querySelector( '[data-variation-message]' );

                    option.setAttribute( 'data-variation', vari.value );
                    option.setAttribute( 'data-variation-price', price );
                    option.setAttribute( 'data-variation-price-euros', euroPrice );
                    option.classList.remove( 'configurator-option--invalid' );
                    messageSlot.innerHTML = `${vari.value} ${formatPrice( is_gbp() ? price : euroPrice, true )}`;

                    this.updateTotal();
                    this.updateFormMessage();
                } );
            } );
        } );
        this.refreshSelection();
    }

    initialisePopovers() {
        const popups = this.querySelectorAll( '[data-popup]' );
        popups.forEach( popup => {
            popup.addEventListener( 'click', ( e ) => {
                const popover = document.getElementById( popup.getAttribute( 'data-popup' ) ?? '' );
                const close = popover.querySelector( '.configurator-option-popover-close' );
                close.addEventListener( 'click', () => {
                    popover.classList.remove( 'is-active' );
                }, { once: true } );

                if ( !popover ) {
                    console.warn( 'No matching popover for this popup: ' + popup.dataset.popup );
                    return;
                }

                document.querySelectorAll( '.configurator-option-popover.is-active' ).forEach( otherFig => {
                    if ( otherFig === popover ) return;
                    otherFig.classList.remove( 'is-active' );
                } );

                popover.classList.toggle( 'is-active' );
            } );
        } );
    }


    /**
     * @returns {boolean} Essentially whether all variations have been chosen or not
     */
    validate() {
        let valid = true;
        this.variableOptions.forEach( option => {
            if ( !is_configured( option ) ) return;

            const chosen = is_variation_chosen( option );
            if ( !chosen ) valid = false;
            option.classList.toggle( 'configurator-option--invalid', !chosen );
        } );
        return valid;
    }

    /**
     * Getter for the currently selected options
     * @returns {HTMLElement[]}
     */
    get selectedOptions() {
        return this.options.filter( is_configured );
    }

    /**
     * @param {number|string} id
     * @returns {?HTMLElement}
     */
    getOption( id ) {
        return this.querySelector( `[data-option="${id}"]` );
    }

    /**
     * @param {number|string} optionId
     * @param {string} name
     * @returns {?HTMLInputElement}
     */
    getVariation( optionId, name ) {
        return this.querySelector( `[data-option="${optionId}"] [data-variation="${name}"]` );
    }

    /**
     *
     * @param {number|string} optionId
     * @param {string} name
     */
    selectVariation( optionId, name ) {
        const variation = this.getVariation( optionId, name );
        if ( !variation ) return;
        variation.checked = true;
        variation.dispatchEvent( new Event( 'input' ) );
    }

    refreshSelection() {
        this.variableOptions.forEach( option => {
            const variation = option.getAttribute( 'data-variation' );
            if ( variation ) {
                this.selectVariation( option.getAttribute( 'data-option' ) ?? '', variation );
            }
        } );
        this.updateTotal();
        this.updateFormMessage();
    }


    /** Getter for the total price, so that the configurators price always reflects the state of the form */
    get total() {
        return this.selectedOptions.reduce(
            ( total, option ) => {
                return total += get_option_total( option );
            },
            this.getBasePrice()
        );
    }


    /**
     * Updates the configurators total HTML with the current formatted total
     */
    updateTotal() {
        this.totalSlot.innerHTML = formatPrice( this.total );
    }


    /**
     * Saves a list of the chosen option IDs to localstorage
     */
    saveOptions() {
        /** @type {ConfiguratorOptions} */
        const items = this.selectedOptions.map( option => {
            const id = option.getAttribute( 'data-option' ) ?? '';
            const variation = option.getAttribute( 'data-variation' );
            const active = is_configured( option );

            return { id, variation, active };
        } );

        localStorage.setItem( this.key, JSON.stringify( items ) );
    }


    /**
     * Loads the previously saved chosen option IDs from localstorage, and activates the corresponding options
     */
    loadOptions() {
        let data = localStorage.getItem( this.key );
        if ( !data ) return;

        const savedOptions = /** @type {ConfiguratorOptions} */ ( JSON.parse( data ) );

        savedOptions.forEach( item => {
            const option = this.getOption( item.id );
            if ( !option || !item.active ) return;
            if ( is_optional( option ) ) option.classList.add( 'configurator-option--active' );
            if ( is_variable( option ) && item.variation ) this.selectVariation( item.id, item.variation );
        } );
        this.updateTotal();
    }

    /**
     * @returns {String} A formatted string detailing the chosen options and total price
     */
    formatOptions() {
        const optionsString = this.selectedOptions.map(
            ( item ) => this.formatOption( item, false )
        ).join( '\n' );

        const message =
            `${this.hutTitle} ${formatPrice( this.total, true, false )}\n\n` +
            `Additional Options: \n` +
            `${optionsString}\n\n` +
            `Total: ${formatPrice( this.total, false, false )}`;

        return message;
    }

    /**
     * @param {HTMLElement} option
     *
     * @returns {String}
     */
    formatOption( option, asHTML = true ) {
        const title = option.querySelector( '.configurator-option-title' )?.textContent;
        const desc = option.querySelector( '.configurator-option-description' )?.textContent ?? '';
        const price = get_option_price( option );

        let formatted = `${title} ${desc} ${formatPrice( price, true, asHTML )}`;

        if ( is_configured( option ) && is_variation_chosen( option ) ) {
            const vari = option.getAttribute( 'data-variation' );
            formatted += ` - ${vari} ${formatPrice( get_variation_price( option ), true, asHTML )}`;
        }

        return formatted.replace( '  ', ' ' );
    }
}

/**
 * @param {HTMLElement} option
 * @returns {number}
 */
function get_option_price( option ) {
    if ( is_optional( option ) && !is_selected( option ) ) {
        return 0;
    }

    const attr = is_gbp() ? 'data-price' : 'data-price-euros';
    return parseFloat( option.getAttribute( attr ) ?? '0' );
}

/**
 * @param {HTMLElement} option
 * @returns {number}
 */
function get_variation_price( option ) {
    if ( !is_variable( option ) ) {
        return 0;
    }

    const attr = is_gbp() ? 'data-variation-price' : 'data-variation-price-euros';
    return parseFloat( option.getAttribute( attr ) ?? '0' );

}

function get_option_total( option ) {
    return get_option_price( option ) + get_variation_price( option );
}

/**
 * Meaning that the field is "active" and should be included in the quote
 *
 * @param {HTMLElement} option
 *
 * @return {boolean}
 */
function is_configured( option ) {
    return is_selected( option ) || ( is_included( option ) && is_variation_chosen( option ) );
}

/**
 * @param {HTMLElement} option
 *
 * @return {boolean}
 */
function is_variation_chosen( option ) {
    return ( option.getAttribute( 'data-variation' ) ?? '' ) !== '';
}

/**
 * @param {HTMLElement} option
 *
 * @return {boolean}
 */
function is_selected( option ) {
    return option.classList.contains( 'configurator-option--active' );
}
/**
 *
 * @param {HTMLElement} option
 * @returns {boolean}
 */
function is_variable( option ) {
    return option.hasAttribute( 'data-variation' );
}

/**
 *
 * @param {HTMLElement} option
 * @returns {boolean}
 */
function is_optional( option ) {
    return option.hasAttribute( 'data-option-optional' );
}

/**
 *
 * @param {HTMLElement} option
 * @returns {boolean}
 */
function is_included( option ) {
    return option.hasAttribute( 'data-option-included' );
}



customElements.define( 'hut-configurator', HutConfigurator );